import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData, getAssignmentSlotForStage } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, parseStageTimerSeconds, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './comic-strip-chaos.css';

const STAGE_NUMBER = 2;
const GAME_DISPLAY_TITLE = 'COMIC STRIP CHAOS';
const PARTICIPATION_POINTS = 1;
const FIELD_MAX = 80;
const PANEL_COUNT = 6;
const DEFAULT_PLAY_SECONDS = 300;
const STAGE10_PLAY_DEADLINE_PREFIX = 'stage10_play_deadline';

const COMIC_IMAGE = `${process.env.PUBLIC_URL || ''}/img/comic.jpg`.replace('//img', '/img');
const LEFT_BUBBLE_IMAGE = `${process.env.PUBLIC_URL || ''}/img/leftbubble.png`.replace('//img', '/img');
const RIGHT_BUBBLE_IMAGE = `${process.env.PUBLIC_URL || ''}/img/rightbubble.png`.replace('//img', '/img');

const VALUE_CATEGORIES = [
  { key: 'respect', label: 'RESPECT' },
  { key: 'leadership', label: 'LEADERSHIP' },
  { key: 'passionate', label: 'PASSIONATE' },
  { key: 'collaboration', label: 'COLLABORATION' },
];

const SCENARIO_CATEGORIES = [
  { key: 'respect', label: 'Respect' },
  { key: 'leadership', label: 'Leadership' },
  { key: 'passion', label: 'Passion' },
  { key: 'collaboration', label: 'Collaboration' },
];

/** Panels 1, 3, 5 show category dialogue; panels 2, 4, 6 are player-filled. */
const PANEL_SPECS = [
  { category: 'respect', mode: 'description' },
  { category: 'respect', mode: 'input' },
  { category: 'collaboration', mode: 'description' },
  { category: 'collaboration', mode: 'input' },
  { category: 'leadership', mode: 'description' },
  { category: 'leadership', mode: 'input' },
];

const INPUT_PANEL_INDICES = PANEL_SPECS.map((spec, i) => (spec.mode === 'input' ? i : null)).filter(
  (i) => i != null
);

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
}

function parseCategoryDialogue(text, label) {
  const blockRe = new RegExp(
    `${label}\\s*:\\s*([\\s\\S]*?)(?=(?:Respect|Integrity|Collaboration|Entrepreneurship)\\s*:|$)`,
    'i'
  );
  const blockMatch = text.match(blockRe);
  if (!blockMatch) {
    return { personA: '', personB: '' };
  }

  const block = blockMatch[1].trim();
  const personAMatch = block.match(/Person\s*A\s*(?:→|:)\s*(.+?)(?=\n\s*Person\s*B|$)/is);
  const personBMatch = block.match(/Person\s*B\s*(?:→|:)\s*(.+?)$/is);

  const personA = personAMatch
    ? personAMatch[1].replace(/\s+/g, ' ').trim()
    : '';
  const personB = personBMatch
    ? personBMatch[1].replace(/\s+/g, ' ').trim()
    : '';

  if (personA || personB) {
    return { personA, personB };
  }

  const plain = block.replace(/\s+/g, ' ').trim();
  return { personA: plain, personB: '' };
}

function parseComicPanels(description) {
  const text = formatStageDescription(description);
  const byCategory = Object.fromEntries(
    SCENARIO_CATEGORIES.map(({ key, label }) => [key, parseCategoryDialogue(text, label)])
  );

  return PANEL_SPECS.map((spec, panelIndex) => {
    const dialogue = byCategory[spec.category] ?? { personA: '', personB: '' };
    const leftText = String(dialogue.personA ?? '').trim();
    const rightText = String(dialogue.personB ?? '').trim();
    const categoryMeta = SCENARIO_CATEGORIES.find((c) => c.key === spec.category);

    return {
      category: spec.category,
      categoryLabel: (categoryMeta?.label ?? spec.category).toUpperCase(),
      mode: spec.mode,
      leftText,
      rightText,
      panelIndex,
    };
  });
}

function createEmptyResponses() {
  return Array.from({ length: INPUT_PANEL_INDICES.length * 2 }, () => '');
}

function inputResponseIndex(panelIndex, side) {
  const inputOrder = INPUT_PANEL_INDICES.indexOf(panelIndex);
  if (inputOrder < 0) return null;
  return inputOrder * 2 + (side === 'right' ? 1 : 0);
}

function isInputPanelComplete(panelIndex, responses) {
  const leftIdx = inputResponseIndex(panelIndex, 'left');
  const rightIdx = inputResponseIndex(panelIndex, 'right');
  if (leftIdx == null || rightIdx == null) return true;
  return (
    String(responses[leftIdx] ?? '').trim().length > 0 &&
    String(responses[rightIdx] ?? '').trim().length > 0
  );
}

function bubbleDomId(panelNumber, side) {
  return `s10-bubble-p${panelNumber}-${side}`;
}

function stage10PlayDeadlineKey(userId, sessionId) {
  return `${STAGE10_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage10PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage10PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage10PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function ClockIcon({ className = 's10-timer-icon' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

export default function Stage10({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(STAGE_NUMBER);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage10 = themeData?.stages_data?.stage_2;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage10?.timer, DEFAULT_PLAY_SECONDS),
    [stage10?.timer]
  );

  const logoUrl = s3PublicUrl(stage10?.logo ?? stage10?.badge);
  const rulesList = getStageRulesList(stage10);
  const subtitle = String(stage10?.title ?? stage10?.name ?? 'Fill The Bubbles, Create The Chaos!');
  const panels = useMemo(
    () => parseComicPanels(stage10?.description),
    [stage10?.description]
  );

  const isConfigured = !!stage10 && String(stage10?.title ?? '').trim().length > 0;

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);

  const [responses, setResponses] = useState(createEmptyResponses);
  const [activePanel, setActivePanel] = useState(0);
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage10,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      setTimerExpired(true);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);
      setTimerExpired(safeRemaining <= 0);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    submitted,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const updateResponse = (panelIndex, side, value) => {
    const responseIndex = inputResponseIndex(panelIndex, side);
    if (responseIndex == null) return;
    setResponses((prev) =>
      prev.map((line, i) =>
        i === responseIndex ? String(value).slice(0, FIELD_MAX) : line
      )
    );
    if (message) setMessage('');
  };

  const buildAnswer = () => {
    const payload = {
      scenarios: PANEL_SPECS.map((spec, index) => {
        const panel = panels[index];
        if (spec.mode === 'description') {
          return {
            category: spec.category,
            mode: 'description',
            personA: panel?.leftText ?? '',
            personB: panel?.rightText ?? '',
          };
        }
        const leftIdx = inputResponseIndex(index, 'left');
        const rightIdx = inputResponseIndex(index, 'right');
        return {
          category: spec.category,
          mode: 'input',
          leftResponse: String(responses[leftIdx] ?? '').trim(),
          rightResponse: String(responses[rightIdx] ?? '').trim(),
        };
      }),
    };
    return JSON.stringify(payload);
  };

  const hasAllEntries = INPUT_PANEL_INDICES.every((index) =>
    isInputPanelComplete(index, responses)
  );

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;

    if (!hasAllEntries && !auto) {
      triggerShake();
      setMessage('Fill every open dialogue bubble before submitting.');
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const finalPoints = PARTICIPATION_POINTS;
    const trimmedAnswer = buildAnswer();

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: true,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
        timeSeconds: stageTimeSeconds,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      const assignedSlot = getAssignmentSlotForStage(STAGE_NUMBER, game);
      if ((!Number.isFinite(nextDay) || nextDay < 1) && assignedSlot == null) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: Number.isFinite(nextDay) ? nextDay + 1 : null,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });
    } catch (err) {
      setSubmitted(false);
      autoSubmitRef.current = false;
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: true,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  const bubblePageStyle = useMemo(
    () => ({
      ...buildStagePageStyle(stageThemeVisuals(themeData), 's10'),
      '--s10-bubble-left-bg': `url(${RIGHT_BUBBLE_IMAGE})`,
      '--s10-bubble-right-bg': `url(${LEFT_BUBBLE_IMAGE})`,
    }),
    [themeData]
  );

  return (
    <div className="stage10-page" style={bubblePageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 2 is not configured: set stage_2 title and description in the theme.
          </p>
        }
      >
        <>
          <div className="stage-page-main stage10-header-main">
            <header className="s10-top-bar">
              <div className="s10-top-timer" aria-label="Stage timer" aria-live="polite">
                <ClockIcon />
                <span className="s10-timer-value">{countdownDisplay}</span>
              </div>
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage10-rules-fab"
                  onClick={openRules}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>
          </div>

          <div className="stage-page-main stage10-main">
            <header className="s10-heading">
              <div className="s10-title-row">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt={GAME_DISPLAY_TITLE}
                    className="s10-game-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <h1 className="s10-brand-title" aria-label={GAME_DISPLAY_TITLE}>
                    <span className="s10-brand-line">COMIC STRIP</span>
                    <span className="s10-brand-chaos">CHAOS</span>
                  </h1>
                )}
              </div>
              {subtitle ? <p className="s10-game-subtitle">{subtitle}</p> : null}
              <p className="s10-screenshot-hint">(Once your comic is complete, capture a screenshot before moving on.)</p>
              <span className="s10-subtitle-line" aria-hidden />
            </header>

            <div className="s10-board">
              <section className="s10-comic-section" aria-label="Comic strip dialogue">
                <form id="s10-comic-form" className="s10-comic-form" onSubmit={handleSubmit}>
                  <div className="s10-comic-row">
                    <div className="s10-comic-stage">
                      <img
                        src={COMIC_IMAGE}
                        alt=""
                        className="s10-comic-image"
                        aria-hidden
                      />
                      <div className="s10-panels-grid">
                      {panels.map((panel, index) => {
                          const panelNum = index + 1;
                          const isDescriptionPanel = panel.mode === 'description';
                          const leftResponseIdx = inputResponseIndex(index, 'left');
                          const rightResponseIdx = inputResponseIndex(index, 'right');
                          const leftResponse =
                            leftResponseIdx != null
                              ? String(responses[leftResponseIdx] ?? '')
                              : '';
                          const rightResponse =
                            rightResponseIdx != null
                              ? String(responses[rightResponseIdx] ?? '')
                              : '';
                          const needsInput =
                            !isDescriptionPanel &&
                            (!leftResponse.trim() || !rightResponse.trim());
                          return (
                          <div
                            key={`panel-${index}`}
                            id={`s10-panel-${panelNum}`}
                            className={`s10-panel s10-panel--${panelNum}${
                              activePanel === index ? ' s10-panel--active' : ''
                            }${inputShake && needsInput ? ' s10-panel--shake' : ''}`}
                            onClick={() => setActivePanel(index)}
                          >
                            <div
                              id={bubbleDomId(panelNum, 'left')}
                              className="s10-bubble s10-bubble--slot-left"
                              title={panel.categoryLabel}
                            >
                              <div className="s10-bubble-content">
                                {isDescriptionPanel ? (
                                  <span className="s10-bubble-text">
                                    {panel.leftText || '—'}
                                  </span>
                                ) : (
                                  <textarea
                                    className="s10-bubble-input"
                                    value={leftResponse}
                                    onChange={(ev) =>
                                      updateResponse(index, 'left', ev.target.value)
                                    }
                                    onClick={(ev) => ev.stopPropagation()}
                                    maxLength={FIELD_MAX}
                                    placeholder="Type here…"
                                    disabled={submitted || timerExpired}
                                    aria-label={`Panel ${panelNum} left dialogue`}
                                    rows={3}
                                  />
                                )}
                              </div>
                            </div>
                            <div
                              id={bubbleDomId(panelNum, 'right')}
                              className="s10-bubble s10-bubble--slot-right"
                              title={panel.categoryLabel}
                            >
                              <div className="s10-bubble-content">
                                {isDescriptionPanel ? (
                                  <span className="s10-bubble-text">
                                    {panel.rightText || '—'}
                                  </span>
                                ) : (
                                  <textarea
                                    className="s10-bubble-input"
                                    value={rightResponse}
                                    onChange={(ev) =>
                                      updateResponse(index, 'right', ev.target.value)
                                    }
                                    onClick={(ev) => ev.stopPropagation()}
                                    maxLength={FIELD_MAX}
                                    placeholder="Type here…"
                                    disabled={submitted || timerExpired}
                                    aria-label={`Panel ${panelNum} right dialogue`}
                                    rows={3}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="s10-values-rail" aria-label="Core values">
                      {VALUE_CATEGORIES.map(({ key, label }) => (
                        <span key={key} className="s10-value-btn">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                </form>
                {message ? <div className="s10-hint">{message}</div> : null}
              </section>

              <div className="stage-page-puzzle-aside stage10-puzzle-aside">
                <StagePuzzleReveal themeData={themeData} />
              </div>
            </div>

            {!submitted ? (
              <div className="s10-submit-wrap">
                <button
                  type="submit"
                  form="s10-comic-form"
                  className="s10-submit-btn"
                  disabled={submitted || !hasAllEntries}
                >
                  Submit
                </button>
              </div>
            ) : null}
          </div>
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage10-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage10-rules-title"
          onClick={closeRules}
        >
          <div className="stage10-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage10-rules-title">How to play</h2>
            <ul className="stage10-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage10-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRevealOverlay ? (
        <div
          className="stage-reveal-overlay stage-reveal-overlay--stage10"
          role="dialog"
          aria-modal="true"
          aria-label="Revealing your puzzle progress"
          onClick={handleRevealOverlayClick}
        >
          <div className="stage-reveal-confetti" aria-hidden>
            {revealConfettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="stage-reveal-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                }}
              />
            ))}
          </div>
          <div className="stage-reveal-stage">
            <aside
              className="stage-reveal-panel s10-puzzle-panel--reveal"
              aria-label="Puzzle pieces progress"
            >
              <StagePuzzleReveal
                themeData={themeData}
                completedDaysOverride={revealCompletedDays}
                animateReveal
              />
            </aside>
          </div>
        </div>
      ) : null}
    </div>
  );
}
